/**
 * T49 — Mobile backend canary.
 *
 * Calls /api/mobile/canary once at app launch, caches the result in
 * memory + localStorage, and exposes a snapshot to the Connection
 * Diagnostics screen in Settings → About so we have on-device proof
 * which routes the running binary's REACT_APP_BACKEND_URL actually
 * serves. Build 70 surfaced a 404 from /api/auth/apple while curl to
 * the same URL returned 422 — the canary closes that visibility gap
 * forever: if the app ever sees `routes.authApple === false`, we
 * know immediately that the deployment is broken (or the .ipa is
 * pointed at a stale host), not Apple's UI.
 *
 * NEVER throws. Always resolves to a snapshot — even on network
 * failure the snapshot is populated with `ok=false`, `error`, and
 * the URL it tried to reach, so the diagnostics screen can render
 * actionable text instead of a blank state.
 */

import api from "./api";

const LS_KEY = "tiplingo_canary_snapshot_v1";

const BUNDLED_BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "<env missing>";

let _inFlight = null;
let _snapshot = _loadFromLS();

function _loadFromLS() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function _writeLS(snap) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(snap));
  } catch {
    /* localStorage full / disabled — non-fatal */
  }
}

/**
 * Force-refresh the canary. Returns the new snapshot. Safe to call
 * concurrently — the first caller wins, the rest share its promise.
 */
export function refreshCanary() {
  if (_inFlight) return _inFlight;
  const startedAt = new Date().toISOString();
  _inFlight = (async () => {
    let snap;
    try {
      const { data } = await api.get(`/mobile/canary?_t=${Date.now()}`, {
        headers: { "Cache-Control": "no-store" },
        timeout: 8000,
      });
      snap = {
        ok: !!data?.ok,
        service: data?.service ?? null,
        routes: data?.routes ?? {},
        backendBuild: data?.build ?? null,
        backendTimestamp: data?.timestamp ?? null,
        bundledBackendUrl: BUNDLED_BACKEND_URL,
        checkedAt: startedAt,
        error: null,
      };
    } catch (e) {
      snap = {
        ok: false,
        service: null,
        routes: {},
        backendBuild: null,
        backendTimestamp: null,
        bundledBackendUrl: BUNDLED_BACKEND_URL,
        checkedAt: startedAt,
        error: {
          status: e?.response?.status ?? null,
          message: e?.message || "network error",
        },
      };
    }
    _snapshot = snap;
    _writeLS(snap);
    _inFlight = null;
    return snap;
  })();
  return _inFlight;
}

/**
 * Synchronous read of the most recent snapshot. Returns null when the
 * canary has never been called and there's nothing in localStorage.
 * Use `refreshCanary()` to force a fresh probe (e.g. on the
 * diagnostics screen's "Re-check" button).
 */
export function getCanarySnapshot() {
  return _snapshot;
}

/**
 * Convenience accessor — true only when we have a snapshot AND the
 * route is reported live. Returns false on missing snapshot so
 * callers can be defensive without checking three nested keys.
 */
export function isRouteLive(routeKey) {
  return !!(_snapshot?.routes && _snapshot.routes[routeKey]);
}
